import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@alight/contracts";
import { cn, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@alight/ui";
import { Maximize2, X } from "lucide-react";
import { KioskCheckIn } from "./attendance";

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/**
 * Full-screen self check-in kiosk for a tablet at the venue.
 *
 * It sits under /admin (so it uses the operator's existing sign-in — no
 * separate student login) but paints a `fixed inset-0` layer OVER the whole
 * portal, so the sidebar/site chrome are hidden and only the "tap your name"
 * grid is visible. The Full screen button additionally requests the browser
 * Fullscreen API to hide the tabs/address bar; Exit returns to the portal.
 */
export function CheckinKioskPage() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const [programId, setProgramId] = React.useState<number | null>(null);

  const programsQuery = useQuery({
    queryKey: ["admin", "att", "programs"],
    queryFn: () => api.adminListPrograms(),
  });
  const active = React.useMemo(
    () =>
      (programsQuery.data ?? [])
        .filter((p) => p.active === 1)
        .sort((a, b) => a.sort_order - b.sort_order),
    [programsQuery.data],
  );

  React.useEffect(() => {
    const first = active[0];
    if (programId === null && first) setProgramId(first.id);
  }, [active, programId]);

  const program = active.find((p) => p.id === programId) ?? null;

  function toggleFullScreen() {
    try {
      if (document.fullscreenElement) void document.exitFullscreen();
      else void document.documentElement.requestFullscreen();
    } catch {
      // Fullscreen may be blocked (e.g. iframe) — the overlay already hides the portal.
    }
  }

  const barButton = cn(
    "inline-flex items-center gap-2 rounded-card border border-border bg-card px-3 py-1.5",
    "text-sm font-semibold text-ink transition-colors hover:bg-paper",
  );

  return (
    <div className="fixed inset-0 z-[60] flex flex-col overflow-auto bg-paper">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-card px-4 py-3">
        <div className="min-w-[14rem] flex-1">
          {active.length > 1 ? (
            <Select
              value={programId != null ? String(programId) : undefined}
              onValueChange={(v) => setProgramId(Number(v))}
            >
              <SelectTrigger className="max-w-xs">
                <SelectValue placeholder="Choose a program" />
              </SelectTrigger>
              <SelectContent>
                {active.map((p) => (
                  <SelectItem key={p.id} value={String(p.id)}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="font-heading text-lg font-bold text-ink">
              {program?.title ?? "Check-in"}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={toggleFullScreen} className={barButton}>
            <Maximize2 className="h-4 w-4" aria-hidden />
            Full screen
          </button>
          <Link to="/admin/attendance" className={barButton}>
            <X className="h-4 w-4" aria-hidden />
            Exit
          </Link>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl flex-1 p-4">
        {programsQuery.isLoading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : program ? (
          <KioskCheckIn api={api} program={program} />
        ) : (
          <p className="text-sm text-muted">
            No running program. Set one to Active in the Programs screen first.
          </p>
        )}
      </div>
    </div>
  );
}
